import { DirectionType } from '~api/som_search';

import { useEffect, useState } from 'react';

import { Button, DateRangePicker, FiltersBar, FormItem, Modal, SidePanel, ToggleButton } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';
import { time, toLocalCalendarDate, toLocalCalendarDateTime, today } from '~src/utils/internationalized-date';

import { CountryMultiSelect } from './country-multi-select';
import { AdvancedFilter } from './useShipmentListFilter';

interface Props {
  onSubmit: (filter: AdvancedFilter) => void;
  defaultValue: AdvancedFilter;
  onReset: () => void;
}

export function FilterDrawer(props: Props) {
  const { defaultValue, onReset, onSubmit } = props;

  const { i18n, t } = useTranslation();
  const [filter, setFilter] = useState(defaultValue);

  const onFieldsChange = (fields: Partial<AdvancedFilter>) => setFilter({ ...filter, ...fields });

  const directionTypeProps = (type: DirectionType) => ({
    onPressedChange: (pressed: boolean) => {
      const newTypes = pressed
        ? [...new Set([...filter.directionTypes, type])]
        : filter.directionTypes.filter(t => t !== type);
      setFilter({ ...filter, directionTypes: newTypes });
    },
    pressed: filter.directionTypes.includes(type),
  });

  useEffect(() => {
    setFilter(defaultValue);
  }, [defaultValue]);

  return (
    <Modal.Root modal={false}>
      <Modal.Trigger>
        <FiltersBar.Button>
          <FiltersBar.MoreIcon />
          {t('common.more')}
        </FiltersBar.Button>
      </Modal.Trigger>
      <SidePanel.Content css={{ width: '400px' }}>
        <Modal.Header title={t('common.filter')} />

        <Modal.Body>
          <form
            id="shipments-filter-form"
            onSubmit={e => {
              e.preventDefault();
              onSubmit(filter);
            }}
            style={{ display: 'grid', gap: '1.5rem' }}
          >
            {/* Created at date range picker */}
            <FormItem.Root>
              <FormItem.Label>{t('common.createdAt')}</FormItem.Label>
              <FormItem.Control>
                <DateRangePicker
                  defaultTime={{ start: time(0, 0), end: time(23, 59) }}
                  granularity="minute"
                  onChange={v =>
                    onFieldsChange({
                      createdAtRange: v
                        ? {
                            start: v.start.toString(),
                            end: v.end.toString(),
                          }
                        : undefined,
                    })
                  }
                  value={
                    filter.createdAtRange
                      ? {
                          start: toLocalCalendarDateTime(filter.createdAtRange.start),
                          end: toLocalCalendarDateTime(filter.createdAtRange.end),
                        }
                      : undefined
                  }
                  today={today('UTC')}
                  locale={i18n.resolvedLanguage}
                  placeholder={t('shipments.list.selectDateOrDateRange')}
                />
              </FormItem.Control>
            </FormItem.Root>
            {/* Shipping date range picker */}
            <FormItem.Root>
              <FormItem.Label>{t('shipments.list.shippingDate')}</FormItem.Label>
              <FormItem.Control>
                <DateRangePicker
                  onChange={v =>
                    onFieldsChange({
                      shippingDateRange: v
                        ? {
                            start: v.start.toString(),
                            end: v.end.toString(),
                          }
                        : undefined,
                    })
                  }
                  value={
                    filter.shippingDateRange
                      ? {
                          start: toLocalCalendarDate(filter.shippingDateRange.start),
                          end: toLocalCalendarDate(filter.shippingDateRange.end),
                        }
                      : undefined
                  }
                  today={today('UTC')}
                  locale={i18n.resolvedLanguage}
                  placeholder={t('shipments.list.selectDateOrDateRange')}
                />
              </FormItem.Control>
            </FormItem.Root>
            {/* Delivery countries multi select */}
            <FormItem.Root>
              <FormItem.Label>{t('shipments.list.deliveryCountries')}</FormItem.Label>
              <FormItem.Control>
                <CountryMultiSelect
                  onValueChange={v => onFieldsChange({ deliveryCountries: v })}
                  placeholder={t('shipments.list.selectDeliveryCountries')}
                  value={filter.deliveryCountries}
                />
              </FormItem.Control>
            </FormItem.Root>
            {/* Origin countries multi select */}
            <FormItem.Root>
              <FormItem.Label>{t('shipments.list.originCountries')}</FormItem.Label>
              <FormItem.Control>
                <CountryMultiSelect
                  onValueChange={v => onFieldsChange({ originCountries: v })}
                  placeholder={t('shipments.list.selectOriginCountries')}
                  value={filter.originCountries}
                />
              </FormItem.Control>
            </FormItem.Root>
            {/* Direction types toggles */}
            <FormItem.Root>
              <FormItem.Label>{t('shipments.list.directionTypes')}</FormItem.Label>
              <div style={{ display: 'flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <FormItem.Control>
                  <>
                    <ToggleButton {...directionTypeProps('OUTBOUND')}>
                      {t('shipments.list.directionType.OUTBOUND')}
                    </ToggleButton>
                    <ToggleButton {...directionTypeProps('RETURN')}>
                      {t('shipments.list.directionType.RETURN')}
                    </ToggleButton>
                    <ToggleButton {...directionTypeProps('UNSPECIFIED')}>
                      {t('shipments.list.directionType.UNSPECIFIED')}
                    </ToggleButton>
                  </>
                </FormItem.Control>
              </div>
            </FormItem.Root>
          </form>
        </Modal.Body>

        <SidePanel.Footer style={{ display: 'flex', flexDirection: 'row', padding: 16 }}>
          <Modal.CloseButton>
            <Button variant="secondary" onClick={onReset}>
              {t('common.cancel')}
            </Button>
          </Modal.CloseButton>
          <Modal.CloseButton>
            <Button form="shipments-filter-form" type="submit">
              {t('common.apply')}
            </Button>
          </Modal.CloseButton>
        </SidePanel.Footer>
      </SidePanel.Content>
    </Modal.Root>
  );
}
